<template>
  <div
    class="rat-mascot"
    :class="{
      'rat-mascot--mascot-only': mascotOnly,
      'rat-mascot--mascot-only-with-bubble': mascotOnly && mascotHelpRevealed,
      'rat-mascot--glass': mascotOnly,
      'rat-mascot--listening': mascotOnly && mascotQuestionListening,
      'rat-mascot--thinking': mascotOnly && mascotOpenAiLoading,
      'rat-mascot--suggest-open': mascotOnly && showQuerySuggestions,
    }"
    role="group"
    :aria-label="ariaLabel"
    :aria-keyshortcuts="mascotOnly ? ariaAssistantSlashKeyshortcut : undefined"
  >
    <template v-if="mascotOnly">
      <header class="rat-mascot-panel-header">
        <h2 class="rat-mascot-panel-title">
          <i class="bi bi-stars" aria-hidden="true" />
          {{ agentDisplayName }}
        </h2>
        <button
          type="button"
          class="rat-mascot-panel-close"
          title="Close assistant"
          aria-label="Close assistant"
          @click.stop="closeMascotPanel"
        >
          <i class="bi bi-x-lg" aria-hidden="true" />
        </button>
      </header>
      <div
        class="rat-mascot-panel-body"
        :class="{ 'rat-mascot-panel-body--suggest-open': showQuerySuggestions }"
      >
        <div
          v-if="mascotHelpRevealed"
          class="rat-mascot-hero rat-mascot-hero--header rat-mascot-hero--active"
        >
          <div class="rat-mascot-chat-row rat-mascot-chat-row--incoming">
            <div
              class="rat-mascot-chat-bubble rat-mascot-mascot-only-bubble rat-mascot-glass-card rat-mascot-glass-card--greeting"
              :aria-busy="mascotTyping ? 'true' : 'false'"
              role="status"
            >
              <p class="rat-mascot-msg-card__label">{{ agentDisplayName }}</p>
              <p class="rat-mascot-mascot-only-bubble__text">
                <span class="rat-mascot-mascot-only-bubble__inner">{{ mascotDisplayedText }}</span><span
                  v-show="mascotTyping"
                  class="rat-mascot-mascot-only-bubble__cursor"
                  aria-hidden="true"
                />
              </p>
            </div>
          </div>
        </div>
        <div
          v-if="mascotHelpRevealed && !mascotTyping"
          class="rat-mascot-mascot-only-followup rat-mascot-glass-stack"
        >
      <div ref="mascotQaMessages" class="rat-mascot-qa-messages" role="log" :aria-label="`Conversation with ${agentDisplayName}`">
        <p v-if="!hasSpeechApi && !mascotDisplayMessages.length" class="rat-mascot-mascot-only-qa-hint rat-mascot-glass-card rat-mascot-glass-card--hint">
          Asking by voice needs Chrome or Edge and a microphone.
        </p>
        <TransitionGroup name="rat-mascot-chat-msg" tag="div" class="rat-mascot-qa-messages__thread">
          <AssistantMascotChatMessage
            v-for="msg in mascotDisplayMessages"
            :key="msg.id"
            :message="msg"
            :agent-display-name="agentDisplayName"
            :show-replay="msg.role === 'assistant' && msg.isLatestAssistant && showTtsReplay"
            @replay="replayMascotTts"
          />
          <div
            v-if="mascotQuestionListening && mascotQHeardLine"
            key="voice-draft"
            class="rat-mascot-chat-row rat-mascot-chat-row--outgoing"
            role="status"
            aria-live="polite"
          >
            <div class="rat-mascot-chat-bubble rat-mascot-mascot-only-bubble rat-mascot-glass-card rat-mascot-glass-card--user rat-mascot-glass-card--voice">
              <p class="rat-mascot-msg-card__label rat-mascot-msg-card__label--outgoing">You</p>
              <p class="rat-mascot-mascot-only-bubble__text rat-mascot-mascot-only-bubble__text--message-body rat-mascot-chat-msg--pending">
                {{ mascotQHeardLine }}
              </p>
            </div>
          </div>
          <div
            v-if="mascotOpenAiLoading"
            key="thinking"
            class="rat-mascot-chat-row rat-mascot-chat-row--incoming"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div class="rat-mascot-chat-bubble rat-mascot-mascot-only-bubble rat-mascot-glass-card rat-mascot-glass-card--reply rat-mascot-glass-card--thinking">
              <p class="rat-mascot-msg-card__label">{{ agentDisplayName }}</p>
              <p class="rat-mascot-mascot-only-bubble__text rat-mascot-mascot-only-bubble__text--message-body rat-mascot-chat-msg--thinking">
                <span class="rat-mascot-thinking-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                Thinking…
              </p>
            </div>
          </div>
        </TransitionGroup>
        <p v-if="mascotOpenAiError" class="rat-mascot-qa__err rat-mascot-glass-card rat-mascot-glass-card--error" role="alert">{{ mascotOpenAiError }}</p>
        <button
          v-if="mascotChatTurns.length && !mascotOpenAiLoading"
          type="button"
          class="rat-mascot-qa__link-btn"
          title="Clear conversation history"
          @click.stop="clearMascotConversation"
        >
          Clear conversation
        </button>
      </div>
      <div
        v-if="showJournalEntryGuideStrip"
        class="rat-mascot-je-guide rat-mascot-glass-card rat-mascot-glass-card--hint"
        aria-label="Journal entry guide"
      >
        <p class="rat-mascot-je-guide__title">
          <i class="bi bi-journal-text" aria-hidden="true" />
          Journal Entry Guide
        </p>
        <p class="rat-mascot-je-guide__hint">Pick a scenario for sample debits &amp; credits (guide only — no balances).</p>
        <div class="rat-mascot-je-guide__chips" role="list">
          <button
            v-for="item in journalEntryGuidePrompts"
            :key="item.id"
            type="button"
            class="rat-mascot-je-guide__chip"
            role="listitem"
            :disabled="mascotOpenAiLoading || mascotQuestionListening"
            @click.stop="askJournalEntryGuide(item)"
          >
            {{ item.text }}
          </button>
        </div>
      </div>
      <div class="rat-mascot-composer rat-mascot-glass-card rat-mascot-glass-card--composer">
        <div
          v-if="showTtsActiveBar"
          class="rat-mascot-tts-bar"
          aria-label="Assistant speech controls"
        >
          <span class="rat-mascot-tts-bar__label">Speaking</span>
          <div class="rat-mascot-tts-bar__btns" role="group" aria-label="Read aloud playback">
            <button
              v-if="mascotTtsUI.speaking && !mascotTtsUI.paused"
              type="button"
              class="rat-mascot-tts__btn"
              @click.stop="pauseMascotSpeech"
            >
              Pause
            </button>
            <button
              v-else-if="mascotTtsUI.paused"
              type="button"
              class="rat-mascot-tts__btn"
              @click.stop="resumeMascotSpeech"
            >
              Resume
            </button>
            <button
              type="button"
              class="rat-mascot-tts__btn rat-mascot-tts__btn--stop"
              @click.stop="stopMascotSpeech"
            >
              Stop
            </button>
          </div>
        </div>
        <p v-if="mascotQuestionListening" class="rat-mascot-composer-status" aria-live="polite">
          <span class="rat-mascot-qa__status-pill">
            <span class="rat-mascot-qa__status-dot" aria-hidden="true" />
            Listening…
          </span>
        </p>
        <form
          class="rat-mascot-qa__form rat-mascot-qa__form--end"
          @submit.prevent="submitMascotTextQuestion"
        >
          <div
            class="rat-mascot-composer__input-wrap"
            :class="{ 'rat-mascot-composer__input-wrap--open': showQuerySuggestions }"
          >
            <ul
              v-if="showQuerySuggestions"
              id="mascot-query-suggestions"
              class="rat-mascot-suggestions rat-mascot-suggestions--inline"
              role="listbox"
              aria-label="Suggestions"
            >
              <li
                v-for="(sug, idx) in querySuggestions"
                :key="sug.id"
                class="rat-mascot-suggestions__item"
                :class="{ 'rat-mascot-suggestions__item--active': idx === querySuggestionIndex }"
                role="option"
                :aria-selected="idx === querySuggestionIndex ? 'true' : 'false'"
                @mousedown.prevent="applyQuerySuggestion(sug)"
              >
                <span class="rat-mascot-suggestions__text">{{ sug.text }}</span>
                <span v-if="sug.category" class="rat-mascot-suggestions__tag">{{ sug.category }}</span>
              </li>
            </ul>
            <div
              class="rat-mascot-composer__field"
              :class="{
                'rat-mascot-composer__field--suggest-open': showQuerySuggestions,
                'rat-mascot-composer__field--listening': mascotQuestionListening,
              }"
            >
              <div v-if="hasSpeechSynthesisTts" class="rat-mascot-composer__field-start">
                <button
                  type="button"
                  class="rat-mascot-composer__speaker"
                  :class="{ 'rat-mascot-composer__speaker--active': speakAloudEnabled }"
                  :title="speakAloudEnabled ? 'Read aloud on — click to turn off' : 'Read aloud off — click to turn on'"
                  :aria-label="speakAloudEnabled ? 'Turn off read aloud' : 'Turn on read aloud'"
                  :aria-pressed="speakAloudEnabled ? 'true' : 'false'"
                  @click.stop="toggleSpeakAloud"
                >
                  <i
                    class="bi"
                    :class="speakAloudEnabled ? 'bi-volume-up' : 'bi-volume-mute'"
                    aria-hidden="true"
                  />
                </button>
              </div>
              <input
                ref="mascotQuestionInput"
                v-model="mascotTextQuestion"
                type="text"
                class="rat-mascot-qa__input"
                :placeholder="mascotChatTurns.length ? 'Ask a follow-up…' : 'Ask anything'"
                :disabled="mascotOpenAiLoading || mascotQuestionListening"
                autocomplete="off"
                aria-label="Question for assistant"
                aria-autocomplete="list"
                :aria-expanded="showQuerySuggestions && querySuggestions.length ? 'true' : 'false'"
                aria-controls="mascot-query-suggestions"
                @input="onQueryInput"
                @focus="onQueryFocus"
                @blur="onQueryBlur"
                @keydown="onQueryKeydown"
              />
              <div class="rat-mascot-composer__field-end">
                <button
                  v-if="hasSpeechApi"
                  type="button"
                  class="rat-mascot-composer__mic"
                  :class="{ 'rat-mascot-composer__mic--active': mascotQuestionListening }"
                  :disabled="mascotOpenAiLoading || mascotQuestionListening"
                  title="Speak your question"
                  aria-label="Speak your question"
                  @click.stop="startMascotQuestionVoice"
                >
                  <i class="bi bi-mic" aria-hidden="true" />
                </button>
                <button
                  type="submit"
                  class="rat-mascot-qa__send"
                  :disabled="!mascotTextQuestionTrimmed || mascotOpenAiLoading || mascotQuestionListening"
                  title="Send message"
                  aria-label="Send message"
                >
                  <i class="bi bi-arrow-up" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
        </div>
      </div>
    </template>
    <div v-if="!mascotOnly" class="rat-mascot-dialog">
      <div class="rat-mascot-bubble">
        <p v-if="greetingTrimmed" class="rat-mascot-greeting">{{ greetingTrimmed }}</p>
        <p v-if="hint" class="rat-mascot-hint">{{ hint }}</p>
        <div
          v-if="active && hasSpeechApi"
          class="rat-mascot-voice"
          :class="{ 'rat-mascot-voice--listening': listening }"
        >
          <span class="rat-mascot-voice-label" aria-live="polite">
            <span v-if="listening" class="rat-mascot-voice-pulse" aria-hidden="true" />
            {{ voiceError ? 'Voice unavailable' : (listening ? 'Listening…' : 'Voice ready') }}
          </span>
          <p v-if="voiceError" class="rat-mascot-hint rat-mascot-hint--warn rat-mascot-hint--voice-err">
            {{ voiceError }}
          </p>
          <button
            v-if="voiceError && hasSpeechApi"
            type="button"
            class="rat-mascot-voice-retry"
            :disabled="voicePermissionLoading"
            @click="retryVoiceInput"
          >
            {{ voicePermissionLoading ? 'Requesting access…' : 'Allow microphone & try again' }}
          </button>
          <p
            v-else-if="!voiceError && (voiceTranscript || voiceInterim)"
            class="rat-mascot-voice-text"
            aria-live="polite"
            aria-relevant="additions"
          >
            <span class="rat-mascot-voice-text-final">{{ voiceTranscript }}</span><span
              v-if="voiceInterim"
              class="rat-mascot-voice-text-interim"
            >{{ voiceInterim }}</span>
          </p>
        </div>
        <p v-else-if="active && !hasSpeechApi" class="rat-mascot-hint rat-mascot-hint--warn">
          {{ voiceError || 'Voice input is not available in this browser. Use Chrome or Edge on desktop.' }}
        </p>
      </div>
      <div v-if="hasDetails" class="rat-mascot-details" aria-label="Consultant details">
        <div class="rat-mascot-details-title">{{ detailsTitle }}</div>
        <dl class="rat-mascot-details-list">
          <template v-for="(row, idx) in detailRows" :key="idx">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </template>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import './AssistantMascot.css';
import ApiService from '@/services/api.service.js';
import { useMascotAssistant } from '@/composables/useMascotAssistant';
import { useJournalEntryGuide } from '@/composables/useJournalEntryGuide';
import { parseAssistantReply } from '@/utils/assistantReplyFormat';
import {
  getQuerySuggestions,
  mergeAssistantSuggestions,
  entitySuggestionFillText,
  inferAssistantRouteContext,
} from '@/utils/assistantQuerySuggestions';
import { JOURNAL_ENTRY_GUIDE_PROMPTS } from '@/utils/assistantToolQuerySuggestions';
import AssistantMascotChatMessage from '@/components/AssistantMascotChatMessage.vue';

const ANNA_AGENT_DISPLAY_NAME = 'ANNA AGENT';

/** macOS / iOS / iPadOS – Cmd vs Ctrl for “modifier + /” shortcuts */
function isAppleLikePlatform() {
  if (typeof navigator === 'undefined') return false;
  const p = navigator.platform || '';
  if (/Mac|iPhone|iPad|iPod/i.test(p)) return true;
  if (navigator.userAgentData && /mac/i.test(String(navigator.userAgentData.platform || ''))) {
    return true;
  }
  const ua = navigator.userAgent || '';
  return /Mac OS X 1\d[._]/i.test(ua) || /\biPhone OS\b/i.test(ua) || /\biPad\b/i.test(ua);
}

function stripMarkdownForSpeech(text) {
  if (typeof text !== 'string' || !text) return '';
  let s = text;
  s = s.replace(/```[\s\S]*?```/g, ' ');
  s = s.replace(/`([^`]+)`/g, '$1');
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
  s = s.replace(/\*([^*]+)\*/g, '$1');
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  s = s.replace(/^#{1,6}\s+/gm, '');
  return s.replace(/\s+/g, ' ').trim();
}

/** Matches Control+/ or Command+/ (and common Intl `key`/`code` variants). */
function matchesControlOrMetaSlashKeydown(e) {
  if (!e || (!e.ctrlKey && !e.metaKey)) return false;
  const k = e.key || '';
  const c = e.code || '';
  return (
    c === 'Slash' ||
    c === 'NumpadDivide' ||
    k === '/' ||
    k === 'Divide' ||
    k === 'NumpadDivide'
  );
}

export default {
  name: 'AssistantMascot',
  components: {
    AssistantMascotChatMessage,
  },
  setup() {
    const { close: closeMascotAssistant } = useMascotAssistant();
    const {
      appendPreviewEntry,
      clearPreview,
      setPreviewLoading,
      consumeAssistantPrompt,
      pendingAssistantPrompt,
      guideClearTick,
    } = useJournalEntryGuide();
    return {
      closeMascotAssistant,
      appendGuidePreviewEntry: appendPreviewEntry,
      clearGuidePreview: clearPreview,
      setGuidePreviewLoading: setPreviewLoading,
      consumeGuideAssistantPrompt: consumeAssistantPrompt,
      guidePendingAssistantPrompt: pendingAssistantPrompt,
      guideClearTick,
    };
  },
  props: {
    /** When true, starts browser speech recognition (user voice) if supported */
    active: {
      type: Boolean,
      default: false,
    },
    greeting: {
      type: String,
      default: 'How can I help you today?',
    },
    /** Chat-only assistant panel (no decorative mascot figure) */
    mascotOnly: {
      type: Boolean,
      default: false,
    },
    /** Logged-in user’s first name (or display name) for the mascot click greeting */
    viewerName: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    detailsTitle: {
      type: String,
      default: 'Consultant in focus',
    },
    detailRows: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      agentDisplayName: ANNA_AGENT_DISPLAY_NAME,
      listening: false,
      voiceTranscript: '',
      voiceInterim: '',
      voiceError: '',
      voicePermissionLoading: false,
      speechRecognition: null,
      /** In mascot-only mode: greeting + chat visible */
      mascotHelpRevealed: false,
      mascotDisplayedText: '',
      mascotTyping: false,
      mascotTypeTimer: null,
      mascotQuestionListening: false,
      mascotQText: '',
      mascotQInterim: '',
      mascotQRecognition: null,
      mascotOpenAiLoading: false,
      mascotAssistantReply: '',
      mascotOpenAiError: '',
      mascotChatTurns: [],
      /** User message sent but reply not yet received */
      mascotPendingUserMessage: '',
      mascotTextQuestion: '',
      mascotLastTtsText: '',
      /** When true, assistant replies are read aloud automatically */
      speakAloudEnabled: false,
      mascotTtsUI: {
        speaking: false,
        paused: false,
      },
      /** Bound handler ref for document capture (Command+/ or Control+/) */
      assistantSlashCaptureHandler: null,
      querySuggestionsOpen: false,
      querySuggestionIndex: -1,
      querySuggestionsBlurTimer: null,
      dbSuggestionRows: [],
      dbSuggestTimer: null,
      dbSuggestRequestId: 0,
    };
  },
  computed: {
    mascotTextQuestionTrimmed() {
      return typeof this.mascotTextQuestion === 'string' ? this.mascotTextQuestion.trim() : '';
    },
    recentUserQuestions() {
      const turns = Array.isArray(this.mascotChatTurns) ? this.mascotChatTurns : [];
      return turns
        .filter((t) => t && t.role === 'user' && typeof t.content === 'string')
        .map((t) => t.content.trim())
        .filter(Boolean)
        .reverse()
        .slice(0, 6);
    },
    querySuggestions() {
      const local = getQuerySuggestions({
        query: this.mascotTextQuestion,
        route: this.$route,
        recentMessages: this.recentUserQuestions,
        entityHints: [],
        limit: 10,
      });
      const db = Array.isArray(this.dbSuggestionRows) ? this.dbSuggestionRows : [];
      return mergeAssistantSuggestions(db, local).slice(0, 10);
    },
    showQuerySuggestions() {
      return (
        this.querySuggestionsOpen &&
        !this.mascotOpenAiLoading &&
        !this.mascotQuestionListening &&
        this.querySuggestions.length > 0
      );
    },
    assistantRouteContext() {
      return inferAssistantRouteContext(this.$route);
    },
    isJournalEntryGuidePage() {
      const name = String(this.$route?.name || '');
      const path = String(this.$route?.path || '').toLowerCase();
      return name === 'SettingsJournalEntryGuide' || path.includes('/settings/journal-entry-guide');
    },
    showJournalEntryGuideStrip() {
      if (!this.mascotOnly || !this.mascotHelpRevealed || this.mascotTyping) return false;
      if (this.mascotOpenAiLoading || this.mascotQuestionListening) return false;
      return this.isJournalEntryGuidePage;
    },
    journalEntryGuidePrompts() {
      return JOURNAL_ENTRY_GUIDE_PROMPTS;
    },
    hasSpeechSynthesisTts() {
      if (typeof window === 'undefined') return false;
      return !!window.speechSynthesis;
    },
    mascotQHeardLine() {
      const a = (this.mascotQText || '').trim();
      const b = (this.mascotQInterim || '').trim();
      if (!a && !b) return '';
      if (!a) return b;
      if (!b) return a;
      return `${a} ${b}`;
    },
    mascotPersonalGreeting() {
      const n = typeof this.viewerName === 'string' ? this.viewerName.trim() : '';
      if (n) {
        return `Hi ${n}, how can I help you?`;
      }
      return 'How can I help you?';
    },
    hasSpeechApi() {
      if (typeof window === 'undefined') return false;
      return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    },
    hasDetails() {
      return Array.isArray(this.detailRows) && this.detailRows.some((r) => r && r.label && r.value);
    },
    greetingTrimmed() {
      return typeof this.greeting === 'string' ? this.greeting.trim() : '';
    },
    mascotDisplayMessages() {
      const turns = Array.isArray(this.mascotChatTurns) ? this.mascotChatTurns : [];
      const items = turns.map((turn, index) => ({
        id: `turn-${index}-${turn.role}`,
        role: turn.role,
        content: turn.content,
        formatted: turn.role === 'assistant' ? parseAssistantReply(turn.content) : null,
        isLatestAssistant:
          turn.role === 'assistant' &&
          index === turns.length - 1 &&
          turns[turns.length - 1]?.role === 'assistant',
        pending: false,
      }));
      const pending = (this.mascotPendingUserMessage || '').trim();
      if (pending) {
        items.push({
          id: 'pending-user',
          role: 'user',
          content: pending,
          formatted: null,
          isLatestAssistant: false,
          pending: true,
        });
      }
      return items;
    },
    showTtsActiveBar() {
      return (
        this.hasSpeechSynthesisTts &&
        this.mascotLastTtsText &&
        (this.mascotTtsUI.speaking || this.mascotTtsUI.paused)
      );
    },
    showTtsReplay() {
      return (
        this.hasSpeechSynthesisTts &&
        this.mascotLastTtsText &&
        !this.mascotTtsUI.speaking &&
        !this.mascotTtsUI.paused
      );
    },
    /** ARIA tokens for global assistant toggles (macOS uses Meta). */
    ariaAssistantSlashKeyshortcut() {
      return isAppleLikePlatform()
        ? 'Meta+Slash Meta+Shift+Slash'
        : 'Control+Slash Control+Shift+Slash';
    },
    ariaLabel() {
      const bits = [];
      if (this.mascotOnly) {
        bits.push('Quick assistant');
        if (this.mascotHelpRevealed) {
          bits.push(this.mascotPersonalGreeting);
        }
        bits.push('Use the close button at the top right or press Control and Slash to close.');
        if (isAppleLikePlatform()) {
          bits.push('On Mac, use Command and Slash instead of Control.');
        }
      } else if (this.greetingTrimmed) {
        bits.push(this.greetingTrimmed);
      } else {
        bits.push('Quick assistant');
      }
      if (this.hasDetails) {
        bits.push(this.detailsTitle);
      }
      return bits.join('. ');
    },
  },
  created() {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      this.speakAloudEnabled = window.localStorage.getItem('anna-assistant-speak-aloud') === '1';
    } catch (e) {
      /* ignore */
    }
  },
  watch: {
    mascotTextQuestion(val) {
      this.scheduleDbSuggestions(val);
    },
    mascotHelpRevealed(val) {
      if (val) {
        this.mascotDisplayedText = '';
        this.$nextTick(() => {
          this.startMascotTypewriter();
        });
      } else {
        this.clearMascotTypewriter();
        this.mascotDisplayedText = '';
        this.stopMascotSpeech();
      }
    },
    active: {
      handler(on) {
        if (this.mascotOnly) {
          this.stopVoiceInput();
          return;
        }
        if (on) {
          this.voiceTranscript = '';
          this.voiceInterim = '';
          this.voiceError = '';
          this.$nextTick(() => {
            this.startVoiceInput();
          });
        } else {
          this.stopVoiceInput();
        }
      },
      immediate: true,
    },
    mascotOnly: {
      immediate: true,
      handler(on) {
        if (typeof document === 'undefined') return;
        if (on) {
          if (!this.assistantSlashCaptureHandler) {
            this.assistantSlashCaptureHandler = (e) => this.onAssistantSlashShortcutCapture(e);
            document.addEventListener('keydown', this.assistantSlashCaptureHandler, true);
          }
          if (!this.mascotHelpRevealed) {
            this.$nextTick(() => this.revealMascotGreeting());
          }
        } else if (this.assistantSlashCaptureHandler) {
          document.removeEventListener('keydown', this.assistantSlashCaptureHandler, true);
          this.assistantSlashCaptureHandler = null;
        }
      },
    },
    mascotChatTurns: {
      handler() {
        if (!this.mascotOnly) return;
        this.$nextTick(() => this.scrollMascotQaToEnd());
      },
      deep: true,
    },
    mascotPendingUserMessage() {
      if (!this.mascotOnly) return;
      this.$nextTick(() => this.scrollMascotQaToEnd());
    },
    mascotOpenAiLoading() {
      if (!this.mascotOnly) return;
      this.$nextTick(() => this.scrollMascotQaToEnd());
    },
    guidePendingAssistantPrompt(prompt) {
      const p = typeof prompt === 'string' ? prompt.trim() : '';
      if (!p || !this.mascotOnly) return;
      this.$nextTick(() => {
        const consumed = this.consumeGuideAssistantPrompt();
        if (!consumed) return;
        if (!this.mascotHelpRevealed) {
          this.revealMascotGreeting();
        }
        if (this.mascotOpenAiLoading) return;
        this.sendMascotQuestionToOpenai(consumed);
      });
    },
    guideClearTick() {
      if (!this.isJournalEntryGuidePage) return;
      this.resetMascotChatState();
    },
  },
  beforeUnmount() {
    if (this.assistantSlashCaptureHandler) {
      document.removeEventListener('keydown', this.assistantSlashCaptureHandler, true);
      this.assistantSlashCaptureHandler = null;
    }
    this.closeQuerySuggestions();
    if (this.dbSuggestTimer) {
      clearTimeout(this.dbSuggestTimer);
      this.dbSuggestTimer = null;
    }
    this.clearMascotTypewriter();
    this.stopMascotQuestionVoice();
    this.stopMascotSpeech();
    this.stopVoiceInput();
  },
  methods: {
    scrollMascotQaToEnd() {
      const el = this.$refs.mascotQaMessages;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    /**
     * Capture-phase listener so macOS (Command+) shortcuts are not swallowed by the
     * browser (e.g. Firefox “Quick Find”) before the app toggles the overlay.
     */
    onAssistantSlashShortcutCapture(e) {
      if (!this.mascotOnly || !matchesControlOrMetaSlashKeydown(e)) return;
      try {
        e.preventDefault();
      } catch (err) {
        /* ignore */
      }
    },
    closeMascotPanel() {
      this.mascotHelpRevealed = false;
      this.mascotAssistantReply = '';
      this.mascotOpenAiError = '';
      this.mascotQText = '';
      this.mascotQInterim = '';
      this.mascotTextQuestion = '';
      this.mascotChatTurns = [];
      this.mascotPendingUserMessage = '';
      this.clearMascotTypewriter();
      this.mascotDisplayedText = '';
      this.stopMascotQuestionVoice();
      this.stopMascotSpeech();
      this.closeMascotAssistant();
    },
    resetMascotChatState() {
      this.mascotAssistantReply = '';
      this.mascotOpenAiError = '';
      this.mascotQText = '';
      this.mascotQInterim = '';
      this.mascotTextQuestion = '';
      this.mascotPendingUserMessage = '';
      this.mascotChatTurns = [];
      this.mascotLastTtsText = '';
      this.stopMascotSpeech();
    },
    clearMascotConversation() {
      this.resetMascotChatState();
      if (this.isJournalEntryGuidePage) {
        this.clearGuidePreview();
      }
    },
    submitMascotTextQuestion() {
      const q = this.mascotTextQuestionTrimmed;
      if (!q) return;
      this.closeQuerySuggestions();
      this.mascotTextQuestion = '';
      this.sendMascotQuestionToOpenai(q);
    },
    askJournalEntryGuide(item) {
      const prompt = item && (item.prompt || item.text);
      if (!prompt) return;
      this.closeQuerySuggestions();
      this.mascotTextQuestion = '';
      if (this.isJournalEntryGuidePage) {
        this.setGuidePreviewLoading(true);
      }
      this.sendMascotQuestionToOpenai(prompt);
    },
    closeQuerySuggestions() {
      this.querySuggestionsOpen = false;
      this.querySuggestionIndex = -1;
      if (this.querySuggestionsBlurTimer) {
        clearTimeout(this.querySuggestionsBlurTimer);
        this.querySuggestionsBlurTimer = null;
      }
    },
    openQuerySuggestions() {
      if (this.mascotOpenAiLoading || this.mascotQuestionListening) return;
      this.querySuggestionsOpen = true;
      this.querySuggestionIndex = -1;
    },
    onQueryInput() {
      this.openQuerySuggestions();
      this.scheduleDbSuggestions(this.mascotTextQuestion);
    },
    scheduleDbSuggestions(raw) {
      if (this.dbSuggestTimer) {
        clearTimeout(this.dbSuggestTimer);
      }
      const q = typeof raw === 'string' ? raw.trim() : '';
      if (q.length < 2) {
        this.dbSuggestionRows = [];
        return;
      }
      this.dbSuggestTimer = setTimeout(() => {
        this.fetchDbSuggestions(q);
      }, 280);
    },
    async fetchDbSuggestions(q) {
      const companyId = this.$route?.params?.id;
      if (!companyId) {
        this.dbSuggestionRows = [];
        return;
      }
      const reqId = ++this.dbSuggestRequestId;
      try {
        const data = await ApiService.fetchAssistantAutocomplete(companyId, q, 8);
        if (reqId !== this.dbSuggestRequestId) return;
        if (this.mascotTextQuestion.trim() !== q) return;
        const rows = Array.isArray(data?.suggestions) ? data.suggestions : [];
        this.dbSuggestionRows = rows.map((row, i) => ({
          id: row.id || `db-${i}`,
          text: row.text,
          category: row.category || 'Match',
          entityType: row.entityType || null,
          source: row.source || 'database',
        }));
        if (this.dbSuggestionRows.length) {
          this.openQuerySuggestions();
        }
      } catch {
        if (reqId === this.dbSuggestRequestId) {
          this.dbSuggestionRows = [];
        }
      }
    },
    onQueryFocus() {
      this.openQuerySuggestions();
    },
    onQueryBlur() {
      if (this.querySuggestionsBlurTimer) {
        clearTimeout(this.querySuggestionsBlurTimer);
      }
      this.querySuggestionsBlurTimer = setTimeout(() => {
        this.closeQuerySuggestions();
      }, 140);
    },
    applyQuerySuggestion(sug) {
      if (!sug || !sug.text) return;
      let text = sug.fillText || sug.text;
      if (
        sug.entityType &&
        (sug.source === 'database' ||
          ['Customer', 'Vendor', 'Consultant', 'Timesheet'].includes(sug.category) ||
          sug.category === 'Match')
      ) {
        text = entitySuggestionFillText(sug.entityType, sug.text, this.$route);
      }
      this.mascotTextQuestion = text;
      this.dbSuggestionRows = [];
      this.closeQuerySuggestions();
      this.$nextTick(() => {
        const el = this.$refs.mascotQuestionInput;
        if (el && typeof el.focus === 'function') {
          el.focus();
          const len = text.length;
          if (typeof el.setSelectionRange === 'function') {
            el.setSelectionRange(len, len);
          }
        }
      });
    },
    onQueryKeydown(e) {
      if (!this.showQuerySuggestions) return;

      const count = this.querySuggestions.length;
      if (!count) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = this.querySuggestionIndex < 0 ? 0 : (this.querySuggestionIndex + 1) % count;
        this.querySuggestionIndex = next;
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev =
          this.querySuggestionIndex <= 0 ? count - 1 : this.querySuggestionIndex - 1;
        this.querySuggestionIndex = prev;
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeQuerySuggestions();
        return;
      }

      if (e.key === 'Enter' && this.querySuggestionIndex >= 0) {
        const picked = this.querySuggestions[this.querySuggestionIndex];
        if (picked) {
          e.preventDefault();
          this.applyQuerySuggestion(picked);
        }
      }
    },
    pauseMascotSpeech() {
      if (typeof window === 'undefined' || !window.speechSynthesis) return;
      const s = window.speechSynthesis;
      if (s.speaking && !s.paused) {
        try {
          s.pause();
        } catch (e) {
          /* some browsers are limited */
        }
        this.mascotTtsUI = { speaking: true, paused: true };
      }
    },
    resumeMascotSpeech() {
      if (typeof window === 'undefined' || !window.speechSynthesis) return;
      if (window.speechSynthesis.paused) {
        try {
          window.speechSynthesis.resume();
        } catch (e) {
          /* ignore */
        }
        this.mascotTtsUI = { speaking: true, paused: false };
      }
    },
    replayMascotTts() {
      if (!this.mascotLastTtsText) return;
      this.speakMascotGreeting(this.mascotLastTtsText);
    },
    toggleSpeakAloud() {
      this.speakAloudEnabled = !this.speakAloudEnabled;
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem('anna-assistant-speak-aloud', this.speakAloudEnabled ? '1' : '0');
        } catch (e) {
          /* ignore */
        }
      }
      if (!this.speakAloudEnabled) {
        this.stopMascotSpeech();
      }
    },
    storeMascotTtsText(text) {
      const t = stripMarkdownForSpeech((text || '').trim());
      if (t) {
        this.mascotLastTtsText = t;
      }
      return t;
    },
    stopMascotQuestionVoice() {
      if (this.mascotQRecognition) {
        this.mascotQRecognition.onend = null;
        this.mascotQRecognition.onresult = null;
        try {
          this.mascotQRecognition.stop();
        } catch (e) {
          /* ignore */
        }
        this.mascotQRecognition = null;
      }
      this.mascotQuestionListening = false;
      this.mascotQInterim = '';
    },
    async startMascotQuestionVoice() {
      if (!this.mascotOnly) return;
      this.stopMascotQuestionVoice();
      this.mascotOpenAiError = '';
      const Ctor = this.getSpeechRecognitionCtor();
      if (!Ctor) {
        this.mascotOpenAiError = 'Voice input is not available in this browser. Use Chrome or Edge.';
        return;
      }
      this.mascotQText = '';
      this.mascotQInterim = '';
      try {
        if (typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        }
      } catch (err) {
        this.mascotOpenAiError = 'Allow the microphone to ask a question by voice.';
        return;
      }
      const rec = new Ctor();
      rec.lang = navigator.language || 'en-US';
      rec.continuous = false;
      rec.interimResults = true;
      rec.onstart = () => {
        this.mascotQuestionListening = true;
      };
      rec.onerror = (ev) => {
        if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
          this.mascotOpenAiError = 'Microphone is blocked. Allow the mic for this site and try again.';
        }
        this.mascotQuestionListening = false;
      };
      rec.onresult = (event) => {
        let finalT = this.mascotQText;
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalT += t;
          } else {
            interim += t;
          }
        }
        this.mascotQText = finalT;
        this.mascotQInterim = interim;
      };
      rec.onend = () => {
        this.mascotQRecognition = null;
        this.mascotQuestionListening = false;
        const raw = (this.mascotQText + (this.mascotQInterim ? ` ${this.mascotQInterim}` : '')).replace(/\s+/g, ' ').trim();
        this.mascotQInterim = '';
        if (raw) {
          this.mascotQText = raw;
          this.sendMascotQuestionToOpenai(raw);
        }
      };
      this.mascotQRecognition = rec;
      try {
        rec.start();
      } catch (e) {
        this.mascotOpenAiError = 'Could not start the microphone. Try again.';
        this.mascotQRecognition = null;
      }
    },
    async sendMascotQuestionToOpenai(userMessage) {
      this.mascotOpenAiError = '';
      this.mascotOpenAiLoading = true;
      this.mascotAssistantReply = '';
      this.mascotPendingUserMessage = userMessage;
      this.mascotQText = '';
      this.mascotQInterim = '';
      if (this.isJournalEntryGuidePage) {
        this.setGuidePreviewLoading(true);
      }
      const history = [...this.mascotChatTurns];
      try {
        const { data } = await ApiService.postMascotAssistantChat({
          message: userMessage,
          history,
          route: this.$route,
        });
        if (data && data.success && data.reply) {
          this.mascotPendingUserMessage = '';
          this.mascotAssistantReply = data.reply;
          this.mascotChatTurns.push({ role: 'user', content: userMessage });
          this.mascotChatTurns.push({ role: 'assistant', content: data.reply });
          if (this.isJournalEntryGuidePage) {
            this.appendGuidePreviewEntry(userMessage, data.reply);
            this.setGuidePreviewLoading(false);
          }
          if (this.mascotChatTurns.length > 20) {
            this.mascotChatTurns = this.mascotChatTurns.slice(-20);
          }
          const ttsText = this.storeMascotTtsText(data.reply);
          if (this.speakAloudEnabled && ttsText) {
            this.speakMascotGreeting(data.reply);
          }
        } else {
          this.mascotChatTurns.push({ role: 'user', content: userMessage });
          this.mascotPendingUserMessage = '';
          this.mascotOpenAiError = (data && data.error) || 'No reply from assistant.';
        }
      } catch (e) {
        this.mascotChatTurns.push({ role: 'user', content: userMessage });
        this.mascotPendingUserMessage = '';
        const msg =
          (e.response && e.response.data && e.response.data.error) || e.message || 'Request failed';
        this.mascotOpenAiError = msg;
      } finally {
        this.mascotOpenAiLoading = false;
        if (this.isJournalEntryGuidePage) {
          this.setGuidePreviewLoading(false);
        }
      }
    },
    stopMascotSpeech() {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try {
          window.speechSynthesis.cancel();
        } catch (e) {
          /* ignore */
        }
      }
      this.mascotTtsUI = { speaking: false, paused: false };
    },
    /**
     * Speak the mascot greeting (text-to-speech). Call from the mascot click / keydown
     * handler so the browser still counts it as a user gesture.
     */
    speakMascotGreeting(text) {
      if (typeof window === 'undefined' || !window.speechSynthesis) {
        return;
      }
      const t = stripMarkdownForSpeech((text || '').trim());
      if (!t) {
        return;
      }
      this.mascotLastTtsText = t;
      this.stopMascotSpeech();
      const synth = window.speechSynthesis;
      const trySpeak = () => {
        const voices = synth.getVoices();
        if (!voices.length) {
          return false;
        }
        try {
          const u = new SpeechSynthesisUtterance(t);
          u.onstart = () => {
            this.mascotTtsUI = { speaking: true, paused: false };
          };
          u.onend = () => {
            this.mascotTtsUI = { speaking: false, paused: false };
          };
          u.onerror = () => {
            this.mascotTtsUI = { speaking: false, paused: false };
          };
          u.lang = (navigator.language || 'en-US').replace('_', '-');
          const prefer =
            voices.find((o) => o.lang && o.lang.toLowerCase().startsWith('en')) ||
            voices.find((o) => o.lang && o.lang.toLowerCase().startsWith(u.lang.split('-')[0].toLowerCase())) ||
            voices[0];
          if (prefer) u.voice = prefer;
          u.rate = 0.98;
          u.pitch = 1.08;
          u.volume = 1;
          synth.speak(u);
          return true;
        } catch (e) {
          return true;
        }
      };
      if (trySpeak()) {
        return;
      }
      const onVoices = () => {
        if (trySpeak()) {
          synth.removeEventListener('voiceschanged', onVoices);
        }
      };
      synth.addEventListener('voiceschanged', onVoices);
      setTimeout(() => {
        synth.removeEventListener('voiceschanged', onVoices);
        trySpeak();
      }, 500);
    },
    clearMascotTypewriter() {
      if (this.mascotTypeTimer) {
        clearInterval(this.mascotTypeTimer);
        this.mascotTypeTimer = null;
      }
      this.mascotTyping = false;
    },
    startMascotTypewriter() {
      this.clearMascotTypewriter();
      const full = this.mascotPersonalGreeting;
      if (!full) {
        return;
      }
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        this.mascotDisplayedText = full;
        return;
      }
      this.mascotTyping = true;
      let i = 0;
      this.mascotTypeTimer = setInterval(() => {
        i += 1;
        this.mascotDisplayedText = full.slice(0, i);
        if (i >= full.length) {
          this.clearMascotTypewriter();
        }
      }, 32);
    },
    revealMascotGreeting() {
      if (!this.mascotOnly || this.mascotHelpRevealed) return;
      this.mascotHelpRevealed = true;
    },
    getSpeechRecognitionCtor() {
      if (typeof window === 'undefined') return null;
      return window.SpeechRecognition || window.webkitSpeechRecognition || null;
    },
    setMicErrorFromDomException(err) {
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        this.voiceError =
          'Microphone is blocked for this site. Click the button below, or use the lock icon in the address bar → Site settings → Microphone → Allow, then try again.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        this.voiceError = 'No microphone was found. Connect a mic and use “try again”.';
      } else {
        this.voiceError = 'Could not use the microphone. Check permissions and try again.';
      }
    },
    async startVoiceInput() {
      this.stopVoiceInput();
      this.voiceError = '';
      this.voicePermissionLoading = true;
      try {
        const Ctor = this.getSpeechRecognitionCtor();
        if (!Ctor) {
          this.voiceError = 'Voice input is not available in this browser. Use Chrome or Edge on desktop.';
          return;
        }
        if (typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
          } catch (err) {
            this.setMicErrorFromDomException(err);
            return;
          }
        }
        const rec = new Ctor();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = navigator.language || 'en-US';
        rec.onstart = () => {
          this.listening = true;
        };
        rec.onend = () => {
          this.listening = false;
          if (this.active && this.speechRecognition) {
            try {
              this.speechRecognition.start();
            } catch (e) {
              /* start may throw if already started */
            }
          }
        };
        rec.onerror = (ev) => {
          if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
            this.voiceError =
              'Microphone is blocked. Use the button below, or the address bar lock → allow microphone for this site.';
            this.stopVoiceInput();
          } else if (ev.error === 'aborted' || ev.error === 'no-speech') {
            /* keep listening in continuous mode */
          }
        };
        rec.onresult = (event) => {
          let interim = '';
          let final = '';
          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const piece = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              final += piece;
            } else {
              interim += piece;
            }
          }
          if (final) {
            const part = final.trim();
            if (part) {
              this.voiceTranscript = this.voiceTranscript
                ? `${this.voiceTranscript} ${part}`
                : part;
            }
          }
          this.voiceInterim = interim;
        };
        this.speechRecognition = rec;
        try {
          rec.start();
        } catch (e) {
          this.voiceError = 'Could not start voice input. Use “Allow microphone & try again”.';
          this.speechRecognition = null;
        }
      } finally {
        this.voicePermissionLoading = false;
      }
    },
    async retryVoiceInput() {
      this.voiceTranscript = '';
      this.voiceInterim = '';
      this.voiceError = '';
      await this.startVoiceInput();
    },
    stopVoiceInput() {
      if (this.speechRecognition) {
        this.speechRecognition.onend = null;
        try {
          this.speechRecognition.stop();
        } catch (e) {
          /* ignore */
        }
        this.speechRecognition = null;
      }
      this.listening = false;
      this.voiceInterim = '';
    },
  },
};
</script>
